import React, { useState, useEffect, useCallback } from 'react';
import SiteNav from './SiteNav';
import './Dashboard.css';

function formatTimestamp(isoString) {
  if (!isoString) return 'Never';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch (e) {
    return 'Unknown';
  }
}

function formatHeartbeatLogs(logLines, hideZeros) {
  const formattedLines = [];
  let lastDate = null;
  for (const line of logLines) {
    const match = line.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})\s+PST\s+(.*)$/);
    if (!match) {
      formattedLines.push(line);
      continue;
    }
    const [, datePart, timePart, rest] = match;
    const [year, month, day] = datePart.split('-');
    const [hour, minute, second] = timePart.split(':');
    const pstDate = new Date(Date.UTC(
      parseInt(year), parseInt(month) - 1, parseInt(day),
      parseInt(hour) + 8, parseInt(minute), parseInt(second || 0)
    ));
    const localDate = pstDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const localTime = pstDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    let formattedSummary = (rest || '').trim().replace('Summary:', '');
    if (hideZeros) {
      formattedSummary = formattedSummary.replace(/\|\s*0\s+sites\s*\|/g, '|').replace(/\|\s*0\s+successful\s*\|/g, '|')
        .replace(/\|\s*0\s+failed\s*\|/g, '|').replace(/\|\s*0\s+skipped\s*\|/g, '|')
        .replace(/\|\s*0\s+changes\s*\|/g, '|').replace(/\|\s*0\s+Heartbeat\s*\|/g, '|')
        .replace(/\|\s*\|/g, '|');
    }
    formattedSummary = formattedSummary.replace(/(\d+) sites/g, '🌐 $1').replace(/(\d+) successful/g, '✅ $1')
      .replace(/(\d+) failed/g, '❌ $1').replace(/(\d+) skipped/g, '⏭️ $1')
      .replace(/(\d+) changes/g, '🔔 $1').replace(/(\d+) Heartbeat/g, '💓 $1');
    if (lastDate !== localDate) {
      if (lastDate !== null) formattedLines.push('');
      formattedLines.push('📅 ' + localDate);
      lastDate = localDate;
    }
    formattedLines.push(`  ${localTime} |${formattedSummary}`);
  }
  return formattedLines.join('\n');
}

function Dashboard({ user, onLogout }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [logLines, setLogLines] = useState(10);
  const [rawLogLines, setRawLogLines] = useState([]);
  const [hideZeros, setHideZeros] = useState(() => typeof localStorage !== 'undefined' && localStorage.getItem('hideZeros') === 'true');
  const [subscribers, setSubscribers] = useState(null);
  const [pendingSubscribers, setPendingSubscribers] = useState(null);
  const [modal, setModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/webscraper.php?action=status', { credentials: 'include' });
      if (response.status === 401) {
        onLogout();
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Failed to fetch status (${response.status})`);
      }
      setStatus(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleRefresh = () => {
    setLoading(true);
    fetchStatus();
  };

  const toggleHideZeros = () => {
    const next = !hideZeros;
    setHideZeros(next);
    if (typeof localStorage !== 'undefined') localStorage.setItem('hideZeros', next);
  };

  const loadSubscribers = useCallback(async () => {
    try {
      const res = await fetch('/api/public.php?action=subscribers', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSubscribers(data.subscribers || {});
      setPendingSubscribers(data.pending_subscribers || {});
    } catch (e) {
      setSubscribers({});
      setPendingSubscribers({});
    }
  }, []);

  // Load subscribers and pending on mount and when status is available (so list refreshes after actions)
  useEffect(() => {
    loadSubscribers();
  }, [loadSubscribers, status?.sites?.list]);

  useEffect(() => {
    if (status?.heartbeat?.recent_entries?.length) {
      setRawLogLines(status.heartbeat.recent_entries);
    } else {
      setRawLogLines([]);
    }
  }, [status?.heartbeat?.recent_entries]);

  const heartbeatLogText = rawLogLines.length
    ? formatHeartbeatLogs(rawLogLines, hideZeros)
    : 'No logs available';

  const toggleSite = async (siteName, enable) => {
    const action = enable ? 'enable' : 'disable';
    try {
      const res = await fetch(`/api/webscraper.php?action=${action}&site=${encodeURIComponent(siteName)}`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to ${action}`);
      await fetchStatus();
    } catch (e) {
      alert('Failed to ' + action + ' site: ' + e.message);
    }
  };

  const updateSiteExpireDate = async (siteName, expireDate) => {
    try {
      const res = await fetch('/api/webscraper.php?action=update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site: siteName, expire_date: expireDate || '' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      await fetchStatus();
    } catch (e) {
      alert('Failed to update expiry: ' + e.message);
    }
  };

  const deleteSite = (siteName) => {
    setModal({
      open: true,
      title: 'Delete Site',
      message: `Are you sure you want to delete "${siteName}"? This will remove all data, hashes, and history for this site.`,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/webscraper.php?action=delete&site=${encodeURIComponent(siteName)}`, { method: 'POST', credentials: 'include' });
          if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            throw new Error(d.error || 'Failed to delete');
          }
          await fetchStatus();
          loadSubscribers();
        } catch (e) {
          alert('Failed to delete site: ' + e.message);
        }
        setModal({ open: false });
      }
    });
  };

  const loadMoreLogs = async () => {
    const next = logLines + 10;
    setLogLines(next);
    try {
      const res = await fetch(`/api/webscraper.php?action=heartbeat&lines=${next}`, { credentials: 'include' });
      const data = await res.json();
      if (data.content) {
        const lines = data.content.split('\n').filter(l => l.trim());
        setRawLogLines(lines);
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (loading && !status) {
    return (
      <div className="dashboard-container">
        <SiteNav user={user} lastUpdate={null} />
        <div className="dashboard-content">
          <div className="loading">Loading dashboard data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <SiteNav user={user} lastUpdate={lastUpdate} />
        <div className="dashboard-content">
          <div className="error-message">
            <p>Error loading data. Please refresh.</p>
            <button onClick={handleRefresh}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const sites = status?.sites?.list || [];
  const heartbeatOk = status?.heartbeat?.status === 'success';

  return (
    <div className="dashboard-container">
      <SiteNav user={user} lastUpdate={lastUpdate} />

      <div className="dashboard-content">
        <div className="summary-cards">
          <div className="card">
            <div className="card-icon">🌐</div>
            <div className="card-content">
              <div className="card-value">{status?.sites?.total ?? 0}</div>
              <div className="card-title">Total Sites</div>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <div className="card-value">{status?.sites?.enabled ?? 0}</div>
              <div className="card-title">Enabled</div>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">💤</div>
            <div className="card-content">
              <div className="card-value">{status?.sites?.disabled ?? 0}</div>
              <div className="card-title">Disabled</div>
            </div>
          </div>
          <div className="card">
            <div className="card-content">
              <div className="card-value">
                {heartbeatOk ? <span className="heartbeat-icon">❤️</span> : '🖤'}
              </div>
              <div className="card-title">Heartbeat</div>
            </div>
          </div>
        </div>

        <div className="section">
          <h2>Monitored Sites</h2>
          <div className="sites-list">
            {sites.map((site, idx) => (
              <div key={idx} className={`site-item ${!site.enabled ? 'disabled' : ''}`}>
                <div className="site-actions">
                  <button
                    type="button"
                    className={`site-toggle-btn ${site.enabled ? 'btn-disable' : 'btn-enable'}`}
                    onClick={() => toggleSite(site.name, !site.enabled)}
                    title={site.enabled ? 'Disable' : 'Enable'}
                  >
                    {site.enabled ? '✕' : '✓'}
                  </button>
                  <button type="button" className="site-delete-btn" onClick={() => deleteSite(site.name)} title="Delete">
                    🗑️
                  </button>
                </div>
                <a href={site.url} target="_blank" rel="noopener noreferrer" className="site-title-link">
                  {site.name}
                </a>
                <div className="site-meta">
                  Extractor: {site.extractor}
                  {site.last_change && <><br />Last Change: {formatTimestamp(site.last_change)}</>}
                  <br />
                  <span className="site-expire-label">Expires: </span>
                  {site.expire_date ? (
                    <span className="site-expire-value">{site.expire_date}</span>
                  ) : (
                    <span className="site-expire-value site-expire-none">—</span>
                  )}
                </div>
                <form
                  className="site-expire-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const input = e.currentTarget.querySelector('input[type="date"]');
                    updateSiteExpireDate(site.name, input ? input.value : '');
                  }}
                >
                  <input
                    type="date"
                    defaultValue={site.expire_date || ''}
                    title="Expiration date (empty = no expiry)"
                  />
                  <button type="submit" className="site-expire-save-btn">Set</button>
                  <button
                    type="button"
                    className="site-expire-clear-btn"
                    onClick={() => updateSiteExpireDate(site.name, '')}
                    title="Clear expiry (no expiry)"
                  >
                    Clear
                  </button>
                </form>
              </div>
            ))}
          </div>
        </div>

        <SubscribersSection subscribers={subscribers} pendingSubscribers={pendingSubscribers} sites={sites} onUpdate={loadSubscribers} />

        <AddSiteSection onAdded={fetchStatus} />

        <div className="section">
          <div className="section-header-row">
            <h2>Recent Heartbeat Logs</h2>
            <button type="button" className="toggle-zeros-btn" onClick={toggleHideZeros}>
              {hideZeros ? 'Show Zeros' : 'Hide Zeros'}
            </button>
          </div>
          <div className="log-container">
            <pre className="log-content">{heartbeatLogText}</pre>
          </div>
          <button type="button" className="load-more-btn" onClick={loadMoreLogs}>
            Load 10 More Logs
          </button>
        </div>
      </div>

      {modal.open && (
        <div className="modal-overlay active" onClick={() => setModal(m => ({ ...m, open: false }))}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{modal.title}</h3>
            <p className="modal-message">{modal.message}</p>
            <div className="modal-actions">
              <button type="button" className="modal-btn modal-btn-cancel" onClick={() => setModal(m => ({ ...m, open: false }))}>
                Cancel
              </button>
              <button type="button" className="modal-btn modal-btn-confirm" onClick={() => { setModal(m => ({ ...m, open: false })); modal.onConfirm && modal.onConfirm(); }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubscribersSection({ subscribers, pendingSubscribers, sites, onUpdate }) {
  const [phone, setPhone] = useState('');
  const [selectedSites, setSelectedSites] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!phone.trim() || selectedSites.length === 0) {
      alert('Please enter a phone number and select at least one site.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/public.php?action=subscribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), sites: selectedSites })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add subscriber');
      setPhone('');
      setSelectedSites([]);
      onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const subs = subscribers && typeof subscribers === 'object' ? subscribers : {};
  const entries = Object.entries(subs);
  const pending = pendingSubscribers && typeof pendingSubscribers === 'object' ? pendingSubscribers : {};
  const pendingEntries = Object.entries(pending);

  const formatPendingDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch (e) {
      return iso;
    }
  };

  return (
    <div className="section">
      <h2>SMS Subscribers</h2>

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Pending (awaiting verification)</h3>
        {pendingEntries.length > 0 ? (
          <div className="subscribers-list">
            {pendingEntries.map(([ph, info]) => (
              <div key={ph} className="subscriber-item subscriber-item-pending">
                <div className="subscriber-header">
                  <span className="subscriber-phone">{ph}</span>
                  <div className="subscriber-header-actions">
                    <button
                      type="button"
                      className="subscriber-remove-btn"
                      onClick={async () => {
                        if (!window.confirm(`Remove pending request for ${ph}?`)) return;
                        const res = await fetch('/api/public.php?action=remove_pending', {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ phone: ph })
                        });
                        if (res.ok) onUpdate();
                        else {
                          const data = await res.json().catch(() => ({}));
                          alert(data.error || 'Failed to remove');
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div className="subscriber-sites">
                  {(info.sites || []).map(s => (
                    <span key={s} className="site-badge">{s}</span>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 6 }}>
                  Requested: {formatPendingDate(info.created_at)}
                  {info.code_expires_at && ` · Code expires: ${formatPendingDate(info.code_expires_at)}`}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#718096' }}>No pending requests.</p>
        )}
      </div>

      <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Subscribed</h3>
      <div style={{ marginBottom: 20 }}>
        {entries.length > 0 ? (
          <div className="subscribers-list">
            {entries.map(([ph, info]) => (
              <div key={ph} className="subscriber-item">
                <div className="subscriber-header">
                  <span className="subscriber-phone">{ph}</span>
                  <div className="subscriber-header-actions">
                    <button
                      type="button"
                      className="subscriber-remove-btn"
                      onClick={async () => {
                        if (!window.confirm(`Remove ${ph} from all site notifications?`)) return;
                        await fetch('/api/public.php?action=unsubscribe', {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ phone: ph })
                        });
                        onUpdate();
                      }}
                    >
                      Remove All
                    </button>
                  </div>
                </div>
                <div className="subscriber-sites">
                  {(info.sites || []).map(s => (
                    <button
                      key={s}
                      type="button"
                      className="site-badge site-badge-clickable"
                      title={`Unsubscribe ${ph} from ${s}`}
                      onClick={async () => {
                        if (!window.confirm(`Remove ${ph} from alerts for "${s}" only?`)) return;
                        const res = await fetch('/api/public.php?action=unsubscribe', {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ phone: ph, sites: [s] })
                        });
                        if (res.ok) onUpdate();
                        else {
                          const data = await res.json().catch(() => ({}));
                          alert(data.error || 'Failed to update');
                        }
                      }}
                    >
                      {s} ×
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#718096' }}>No subscribers yet.</p>
        )}
      </div>
      <form className="add-site-form" onSubmit={handleAdd} style={{ maxWidth: 600 }}>
        <div className="form-group">
          <label htmlFor="subscriberPhone">Add Subscriber (admin bypass — no double opt-in)</label>
          <input
            type="tel"
            id="subscriberPhone"
            placeholder="+1 555 123 4567"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            required
          />
          <small style={{ color: '#718096', display: 'block', marginTop: 4 }}>US phone numbers only (include +1)</small>
        </div>
        <div className="form-group">
          <label>Select Sites</label>
          <div className="admin-checkboxes">
            {sites.length === 0 ? (
              <p style={{ color: '#718096' }}>No sites available.</p>
            ) : (
              sites.map(site => (
                <label key={site.name}>
                  <input
                    type="checkbox"
                    checked={selectedSites.includes(site.name)}
                    onChange={e => setSelectedSites(prev => e.target.checked ? [...prev, site.name] : prev.filter(x => x !== site.name))}
                  />
                  <span>{site.name}</span>
                </label>
              ))
            )}
          </div>
        </div>
        <button type="submit" className="add-site-btn" disabled={submitting}>
          {submitting ? 'Adding...' : 'Add Subscriber'}
        </button>
      </form>
    </div>
  );
}

function AddSiteSection({ onAdded }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      alert('URL is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/webscraper.php?action=add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), name: name.trim() || undefined })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add site');
      setUrl('');
      setName('');
      onAdded();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="section">
      <h2>Add New Site</h2>
      <form className="add-site-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="siteUrl">URL *</label>
          <input id="siteUrl" type="text" placeholder="example.com" value={url} onChange={e => setUrl(e.target.value)} required />
        </div>
        <div className="form-group">
          <label htmlFor="siteName">Name (optional - derived from URL if blank)</label>
          <input id="siteName" type="text" placeholder="Leave blank to auto-generate" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <button type="submit" className="add-site-btn" disabled={submitting}>
          {submitting ? 'Adding...' : 'Add Site'}
        </button>
      </form>
    </div>
  );
}

export default Dashboard;
